import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Scissors, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Gift, 
  Globe,
  Code,
  Building2,
  ChevronRight,
  Play,
  CheckCircle,
  Target,
  Zap,
  Shield,
  Database,
  CreditCard,
  BarChart3,
  Mail,
  Linkedin,
  ArrowRight,
  Star,
  Crown,
  Sparkles,
  Heart,
  Calendar,
  Download,
  FileText,
  Loader2,
  Lock,
  Unlock,
  User,
  Building,
  X,
  QrCode,
  Smartphone,
  Clock,
  Trophy,
  Share2,
  Copy,
  Twitter,
  Facebook,
  MessageCircle
} from "lucide-react";
import html2pdf from 'html2pdf.js';
import axios from 'axios';
import { toast } from 'sonner';
import SEOMeta from "@/components/SEOMeta";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const InvestorsPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    company: ''
  });
  const deckRef = useRef(null);
  
  // Share functionality
  const pageUrl = "https://myhairchair.com/investors";
  const shareTitle = "MyHairChair - Investor Opportunity | $18M ARR by Year 3";
  const shareDescription = "Join us in disrupting the $97B salon industry. 25% equity opportunity with projected $18M ARR by 2028.";
  
  const handleShare = async (platform) => {
    const shareData = {
      title: shareTitle,
      text: shareDescription,
      url: pageUrl
    };
    
    switch(platform) {
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share(shareData);
            toast.success('Shared successfully!');
          } catch (err) {
            if (err.name !== 'AbortError') {
              handleShare('copy');
            }
          }
        } else {
          handleShare('copy');
        }
        break;
      case 'copy':
        navigator.clipboard.writeText(pageUrl);
        toast.success('Link copied to clipboard!');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + pageUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareDescription + '\n\n' + pageUrl)}`, '_blank');
        break;
      default:
        break;
    }
  };

  // Fetch platform stats and check access
  useEffect(() => {
    const accessGranted = localStorage.getItem('investor_access');
    if (accessGranted) {
      setHasAccess(true);
    }
    
    // Fetch live platform stats
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/api/investors/stats`);
        setPlatformStats(response.data);
      } catch (error) {
        console.error('Failed to fetch platform stats:', error);
      }
    };
    fetchStats();
  }, []);

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/investors/leads`, leadForm);
      if (response.data.access_granted) {
        setHasAccess(true);
        localStorage.setItem('investor_access', 'true');
        localStorage.setItem('investor_name', leadForm.name);
        setShowAccessForm(false);
        toast.success('Access granted! Welcome to MyHairChair.');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = () => {
    if (hasAccess) {
      generatePDF();
    } else {
      setShowAccessForm(true);
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    
    // Create a dedicated PDF content div
    const pdfContent = document.createElement('div');
    pdfContent.innerHTML = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Helvetica', 'Arial', sans-serif; }
        .slide { 
          width: 100%; 
          min-height: 100vh; 
          padding: 60px; 
          page-break-after: always;
          background: linear-gradient(135deg, #000 0%, #1a1a1a 100%);
          color: white;
        }
        .slide:last-child { page-break-after: avoid; }
        .gold { color: #f59e0b; }
        .gold-bg { background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%); }
        .title { font-size: 48px; font-weight: bold; margin-bottom: 20px; }
        .subtitle { font-size: 24px; color: #9ca3af; margin-bottom: 40px; }
        .stat-box { 
          background: rgba(245, 158, 11, 0.1); 
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 16px; 
          padding: 30px; 
          text-align: center;
          margin: 10px;
        }
        .stat-value { font-size: 42px; font-weight: bold; color: #f59e0b; }
        .stat-label { font-size: 14px; color: #9ca3af; margin-top: 8px; }
        .card { 
          background: rgba(30, 30, 30, 0.8); 
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 16px; 
          padding: 30px; 
          margin: 15px 0;
        }
        .card-title { font-size: 24px; font-weight: bold; margin-bottom: 15px; }
        .card-text { font-size: 16px; color: #d1d5db; line-height: 1.6; }
        .flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .justify-between { justify-content: space-between; }
        .items-center { align-items: center; }
        .text-center { text-align: center; }
        .mb-4 { margin-bottom: 16px; }
        .mb-8 { margin-bottom: 32px; }
        .mt-8 { margin-top: 32px; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
        .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
        .check { color: #10b981; margin-right: 10px; }
        .bullet { 
          width: 8px; 
          height: 8px; 
          background: #f59e0b; 
          border-radius: 50%; 
          display: inline-block;
          margin-right: 12px;
        }
        .progress-bar { 
          height: 20px; 
          background: #374151; 
          border-radius: 10px; 
          overflow: hidden;
          margin: 20px 0;
        }
        .progress-fill { 
          height: 100%; 
          width: 75%; 
          background: linear-gradient(90deg, #f59e0b 0%, #eab308 100%); 
          border-radius: 10px;
        }
        .logo-circle {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: black;
          font-weight: bold;
          margin-bottom: 20px;
        }
        .founder-circle {
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 48px;
          color: black;
          font-weight: bold;
          margin: 0 auto 20px;
        }
        .section-label {
          color: #f59e0b;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 16px;
        }
        .highlight-box {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 12px;
          padding: 20px 30px;
          display: inline-block;
          margin-top: 30px;
        }
        .red { color: #ef4444; }
        .green { color: #10b981; }
        .blue { color: #3b82f6; }
        .purple { color: #a855f7; }
        .orange { color: #f97316; }
      </style>

      <!-- Slide 1: Title -->
      <div class="slide">
        <div style="display: flex; align-items: center; gap: 20px; margin-bottom: 60px;">
          <div class="logo-circle">✂️</div>
          <div>
            <div style="font-size: 28px; font-weight: bold; color: #f59e0b;">MyHairChair</div>
            <div style="font-size: 14px; color: #6b7280;">Investor Presentation 2025</div>
          </div>
        </div>
        <h1 class="title">MyHairChair</h1>
        <h2 style="font-size: 36px; color: #f59e0b; margin-bottom: 30px;">The Future of Salon Commerce</h2>
        <p class="subtitle">Empowering the Art of the Chair</p>
        <p style="font-size: 24px; color: #fcd34d; font-style: italic; margin-bottom: 40px;">"The Chair is the center of the beauty economy."</p>
        <p class="card-text" style="max-width: 800px; font-size: 18px;">
          MyHairChair is a comprehensive ecosystem designed for the modern era of travel, leisure, and professional grooming. 
          We aren't just an app — <span class="gold">we are the infrastructure for the $500B+ global beauty and leisure market.</span>
        </p>
        <div style="margin-top: 60px; padding: 20px; background: rgba(255,255,255,0.05); border-radius: 12px; display: inline-block;">
          <p style="color: #9ca3af;">Presented by</p>
          <p style="font-size: 20px; font-weight: bold;">George Broughton III</p>
          <p class="gold">Founder & CEO</p>
          <p style="font-size: 12px; color: #6b7280;">MyHairChair | A Gonowbaby Media Group LLC Company</p>
        </div>
      </div>

      <!-- Slide 2: Market Stats -->
      <div class="slide" style="background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%); color: black;">
        <h1 class="title" style="color: black;">Market Opportunity</h1>
        <p style="font-size: 20px; color: rgba(0,0,0,0.7); margin-bottom: 50px;">The numbers speak for themselves</p>
        <div class="grid-4">
          <div style="background: rgba(0,0,0,0.1); border-radius: 16px; padding: 30px; text-align: center;">
            <div style="font-size: 48px; font-weight: bold;">$500B+</div>
            <div style="font-size: 14px; margin-top: 10px;">Global Beauty Market</div>
          </div>
          <div style="background: rgba(0,0,0,0.1); border-radius: 16px; padding: 30px; text-align: center;">
            <div style="font-size: 48px; font-weight: bold;">$7.5B</div>
            <div style="font-size: 14px; margin-top: 10px;">Black Beauty Spend (Annual)</div>
          </div>
          <div style="background: rgba(0,0,0,0.1); border-radius: 16px; padding: 30px; text-align: center;">
            <div style="font-size: 48px; font-weight: bold;">5th</div>
            <div style="font-size: 14px; margin-top: 10px;">Latino GDP World Rank</div>
          </div>
          <div style="background: rgba(0,0,0,0.1); border-radius: 16px; padding: 30px; text-align: center;">
            <div style="font-size: 48px; font-weight: bold;">9x</div>
            <div style="font-size: 14px; margin-top: 10px;">Higher Per-Capita Spend</div>
          </div>
        </div>
      </div>

      <!-- Slide 3: The Problem -->
      <div class="slide">
        <div class="section-label red">The Problem</div>
        <h1 class="title">The <span class="red">Friction</span> in the Industry</h1>
        <p class="subtitle">The beauty industry operates on outdated systems</p>
        <div class="grid-2" style="grid-template-columns: 1fr 1fr 1fr; margin-top: 40px;">
          <div class="card" style="border-color: rgba(239, 68, 68, 0.3);">
            <div style="font-size: 36px; margin-bottom: 15px;">🏢</div>
            <div class="card-title">Salon Owners</div>
            <p class="card-text">Struggling to track and collect booth rent manually. Chasing payments, paper calendars, and missed revenue.</p>
          </div>
          <div class="card" style="border-color: rgba(239, 68, 68, 0.3);">
            <div style="font-size: 36px; margin-bottom: 15px;">✂️</div>
            <div class="card-title">Stylists & Barbers</div>
            <p class="card-text">Lack professional tools to manage loyalty programs, payments, and client relationships.</p>
          </div>
          <div class="card" style="border-color: rgba(239, 68, 68, 0.3);">
            <div style="font-size: 36px; margin-bottom: 15px;">👥</div>
            <div class="card-title">Clients</div>
            <p class="card-text">No central portal to manage their beauty leisure experience, track rewards, or book seamlessly.</p>
          </div>
        </div>
      </div>

      <!-- Slide 4: The Solution -->
      <div class="slide">
        <div class="section-label green">The Solution</div>
        <h1 class="title">MyHairChair <span class="green">Solves Everything</span></h1>
        <p class="subtitle">A complete platform connecting owners, stylists, and clients</p>
        <div class="grid-2" style="margin-top: 40px;">
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 32px; margin-bottom: 15px;">💰</div>
            <div class="card-title">Automated Rent Collection</div>
            <p class="card-text">Seamless booth rent collection with recurring billing, reminders, and instant payment tracking.</p>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 32px; margin-bottom: 15px;">🎯</div>
            <div class="card-title">The Command Center</div>
            <p class="card-text">A personal business suite for independent stylists - services, pricing, scheduling, and client management.</p>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 32px; margin-bottom: 15px;">🎁</div>
            <div class="card-title">Loyalty Engine</div>
            <p class="card-text">Customizable point systems that drive retention. Stylists set their values, owners control commissions.</p>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 32px; margin-bottom: 15px;">📅</div>
            <div class="card-title">Leisure Integration</div>
            <p class="card-text">Booking built for the entertainment and travel lifestyle. Beauty is the first step in the leisure journey.</p>
          </div>
        </div>
      </div>

      <!-- Slide 5: Loyalty Engine -->
      <div class="slide">
        <div class="section-label gold">The Innovation</div>
        <h1 class="title">The <span class="gold">"Loyalty Engine"</span></h1>
        <div class="grid-2" style="margin-top: 40px;">
          <div>
            <p class="card-text mb-8" style="font-size: 18px;">Our proprietary loyalty system puts the power in the hands of the professionals while maximizing client retention.</p>
            <div style="margin-bottom: 20px;">
              <span class="check">✓</span>
              <strong>Empowered Stylists:</strong> Independent pros set their own point values
            </div>
            <div style="margin-bottom: 20px;">
              <span class="check">✓</span>
              <strong>Owner Control:</strong> Master-switch for commission-based or booth-rent models
            </div>
            <div style="margin-bottom: 20px;">
              <span class="check">✓</span>
              <strong>Smart Exclusions:</strong> Protect margins by excluding high-labor services
            </div>
            <div style="margin-bottom: 20px;">
              <span class="check">✓</span>
              <strong>Status-Based Rewards:</strong> Tier system (Bronze → Platinum) drives repeat visits
            </div>
          </div>
          <div class="card">
            <div class="text-center mb-4">
              <div style="font-size: 48px;">🎁</div>
              <div class="card-title">Point Tracking System</div>
              <p style="color: #9ca3af;">Client Progress to Free Service</p>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <span style="color: #9ca3af;">Current Points</span>
              <span class="gold" style="font-weight: bold;">75 / 100</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
            <p style="font-size: 12px; color: #6b7280;">25 points to FREE Haircut!</p>
            <div style="display: flex; justify-content: space-between; margin-top: 20px;">
              <div style="text-align: center; padding: 10px; background: rgba(245, 158, 11, 0.2); border-radius: 8px; flex: 1; margin: 0 5px;">
                <div style="font-size: 20px;">👑</div>
                <div style="font-size: 12px; color: #f59e0b;">Bronze</div>
              </div>
              <div style="text-align: center; padding: 10px; background: rgba(245, 158, 11, 0.2); border-radius: 8px; flex: 1; margin: 0 5px;">
                <div style="font-size: 20px;">👑</div>
                <div style="font-size: 12px; color: #f59e0b;">Silver</div>
              </div>
              <div style="text-align: center; padding: 10px; background: rgba(55, 65, 81, 0.5); border-radius: 8px; flex: 1; margin: 0 5px;">
                <div style="font-size: 20px;">👑</div>
                <div style="font-size: 12px; color: #6b7280;">Gold</div>
              </div>
              <div style="text-align: center; padding: 10px; background: rgba(55, 65, 81, 0.5); border-radius: 8px; flex: 1; margin: 0 5px;">
                <div style="font-size: 20px;">👑</div>
                <div style="font-size: 12px; color: #6b7280;">Platinum</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Slide 6: Power Consumer -->
      <div class="slide">
        <div class="section-label gold">Market Opportunity</div>
        <h1 class="title">The <span class="gold">"Power Consumer"</span></h1>
        <p class="subtitle">We target the highest-spending demographics in personal care</p>
        <div class="grid-2" style="margin-top: 40px;">
          <div class="card" style="border-color: rgba(168, 85, 247, 0.3);">
            <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
              <div style="font-size: 36px;">👑</div>
              <div>
                <div class="card-title" style="margin-bottom: 0;">Black & African American</div>
                <div class="purple">The "Power" Consumer</div>
              </div>
            </div>
            <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 20px; margin-bottom: 20px;">
              <div style="font-size: 32px; font-weight: bold;">$7.5 Billion</div>
              <div style="font-size: 14px; color: #9ca3af;">Annual beauty spend by Black women alone</div>
            </div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>9x higher per-capita spend on hair care</div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>Cultural trendsetters - influence what becomes 'cool'</div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>Highly responsive to loyalty & status-based rewards</div>
            <div><span class="bullet"></span>Most engaged with beauty technology adoption</div>
          </div>
          <div class="card" style="border-color: rgba(249, 115, 22, 0.3);">
            <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
              <div style="font-size: 36px;">📈</div>
              <div>
                <div class="card-title" style="margin-bottom: 0;">Hispanic & Latino</div>
                <div class="orange">The Fastest-Growing Consumer</div>
              </div>
            </div>
            <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 20px; margin-bottom: 20px;">
              <div style="font-size: 32px; font-weight: bold;">5th Largest GDP</div>
              <div style="font-size: 14px; color: #9ca3af;">If U.S. Latino market were a standalone country</div>
            </div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>Median age 30 vs. 44 (prime spending years)</div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>Engine of American economy's growth</div>
            <div style="margin-bottom: 10px;"><span class="bullet"></span>#1 consumers of cinema & family entertainment</div>
            <div><span class="bullet"></span>High adoption of mobile-first experiences</div>
          </div>
        </div>
        <div class="highlight-box text-center" style="display: block; margin-top: 40px;">
          <p class="gold" style="font-size: 18px; font-weight: bold;">The MyHairChair Advantage: We speak the language of the "Power Consumer."</p>
        </div>
      </div>

      <!-- Slide 7: Our Edge -->
      <div class="slide">
        <div class="section-label blue">Leadership Experience</div>
        <h1 class="title">The <span class="blue">Founder's</span> Advantage</h1>
        <div class="grid-2" style="margin-top: 40px;">
          <div class="card" style="border-color: rgba(59, 130, 246, 0.3);">
            <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
              <div style="font-size: 36px;">🛡️</div>
              <div>
                <div class="card-title" style="margin-bottom: 0;">Sterile Mate LLC</div>
                <div class="blue">B2B Medical Supplies (Est. 2020)</div>
              </div>
            </div>
            <p class="card-text mb-4">Founded by George Broughton III during the COVID-19 pandemic, Sterile Mate is a <span class="blue">separate B2B venture</span> focused on <span class="blue">medical supplies for hospitals</span> and <span class="blue">water treatment systems</span>.</p>
            <div style="margin-bottom: 10px;"><span class="check">✓</span>Proven entrepreneurial track record</div>
            <div style="margin-bottom: 10px;"><span class="check">✓</span>Industry-standard professionalism</div>
            <div><span class="check">✓</span>B2B operations experience</div>
          </div>
          <div class="card" style="border-color: rgba(236, 72, 153, 0.3);">
            <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
              <div style="font-size: 36px;">🌐</div>
              <div>
                <div class="card-title" style="margin-bottom: 0;">Gonowbaby Media Group LLC</div>
                <div style="color: #ec4899;">Travel & Entertainment Network</div>
              </div>
            </div>
            <p class="card-text mb-4">MyHairChair's parent company. Integrated with Gonowbaby's travel and entertainment network, we position <span style="color: #ec4899;">beauty as the first step in the "Leisure Journey"</span>.</p>
            <div style="margin-bottom: 10px;"><span class="check">✓</span>Travel & entertainment reach</div>
            <div style="margin-bottom: 10px;"><span class="check">✓</span>Leisure sector partnerships</div>
            <div><span class="check">✓</span>Multi-channel marketing network</div>
          </div>
        </div>
        <p style="text-align: center; font-size: 20px; margin-top: 40px;">
          <span class="gold" style="font-weight: bold;">The Synergy:</span> Beauty is the first step in the "Leisure Journey."
        </p>
      </div>

      <!-- Slide 8: Competitive Analysis -->
      <div class="slide">
        <div class="section-label purple">Market Position</div>
        <h1 class="title">Competitive <span class="purple">Analysis</span></h1>
        <p class="subtitle">23 Unique Features That Set Us Apart</p>
        
        <div style="overflow-x: auto; margin: 20px 0;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background: rgba(168, 85, 247, 0.2);">
                <th style="padding: 8px; text-align: left; border-bottom: 2px solid #a855f7;">Feature</th>
                <th style="padding: 8px; text-align: center; border-bottom: 2px solid #a855f7; color: #f59e0b; font-weight: bold;">MyHairChair</th>
                <th style="padding: 8px; text-align: center; border-bottom: 2px solid #a855f7;">Booksy</th>
                <th style="padding: 8px; text-align: center; border-bottom: 2px solid #a855f7;">StyleSeat</th>
                <th style="padding: 8px; text-align: center; border-bottom: 2px solid #a855f7;">Vagaro</th>
                <th style="padding: 8px; text-align: center; border-bottom: 2px solid #a855f7;">Square</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Guest Booking (No Account)</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Recurring Appointments</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">QR Code Client Invites</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Digital Business Cards</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Digital Marketing Toolkit</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.2);">
                <td style="padding: 6px; font-weight: bold;">🤖 AI Receipt Verification</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓ NEW</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Pro Mini-Site Builder</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Walk-in Queue Management</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Multi-Tier Status System</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Magic Link (Passwordless)</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Profile Verification System</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">PWA (App-Like Experience)</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">AI Chatbot Assistant</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">Booth/Chair Rental Mgmt</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 Scalp Care & Health Logs</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 Smart Service Upsells</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 Staff Payroll Management</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 Affiliate Program (Soldiers)</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 AI Video Tutorials</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151; background: rgba(245, 158, 11, 0.1);">
                <td style="padding: 6px; font-weight: bold;">🆕 Manual Payment Receipts</td>
                <td style="padding: 6px; text-align: center; color: #10b981; font-weight: bold;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
              <tr style="border-bottom: 1px solid #374151;">
                <td style="padding: 6px;">Stylist Marketplace</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #10b981;">✓</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
                <td style="padding: 6px; text-align: center; color: #ef4444;">✗</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="highlight-box" style="display: block; text-align: center;">
          <span style="font-size: 18px; color: #f59e0b; font-weight: bold;">🏆 23 Unique Features</span>
          <span style="color: #d1d5db;"> that no single competitor offers together</span>
        </div>
      </div>

      <!-- Slide 9: Marketing Strategy -->
      <div class="slide">
        <div style="color: #f97316; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Go-To-Market Strategy</div>
        <h1 class="title">How We <span style="color: #f97316;">Grow</span></h1>
        <p class="subtitle">Word-of-mouth driven growth powered by industry insiders</p>
        
        <div class="card" style="border-color: rgba(249, 115, 22, 0.4); padding: 30px; margin: 20px 0; background: linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(239, 68, 68, 0.1));">
          <div style="display: flex; align-items: center; gap: 30px;">
            <div style="flex: 1;">
              <div style="background: rgba(249, 115, 22, 0.2); color: #f97316; font-size: 12px; padding: 5px 15px; border-radius: 20px; display: inline-block; margin-bottom: 15px;">🛡️ Our Secret Weapon</div>
              <h3 style="font-size: 28px; font-weight: bold; margin-bottom: 10px;">HairCare <span style="color: #f97316;">Soldiers</span></h3>
              <p style="color: #9ca3af; margin-bottom: 15px;">An elite network of salon stylists, barbers, and owners who champion MyHairChair. They earn <span style="color: #10b981; font-weight: bold;">lifetime revenue</span> for every referral.</p>
              <div style="display: flex; gap: 20px;">
                <div style="background: rgba(0,0,0,0.3); padding: 10px 20px; border-radius: 8px;">
                  <span style="color: #f97316; font-weight: bold; font-size: 24px;">50</span>
                  <span style="color: #9ca3af; font-size: 12px; margin-left: 8px;">Soldiers in Key Markets</span>
                </div>
                <div style="background: rgba(0,0,0,0.3); padding: 10px 20px; border-radius: 8px;">
                  <span style="color: #10b981; font-weight: bold; font-size: 24px;">∞</span>
                  <span style="color: #9ca3af; font-size: 12px; margin-left: 8px;">Lifetime Commissions</span>
                </div>
              </div>
            </div>
            <div style="width: 120px; height: 120px; background: linear-gradient(135deg, #f97316, #ef4444); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
              <div style="text-align: center; color: white;">
                <div style="font-size: 36px;">🛡️</div>
                <div style="font-size: 12px; font-weight: bold;">SOLDIERS</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Super Soldier "Power 2" Executive Tier -->
        <div class="card" style="border-color: rgba(168, 85, 247, 0.4); padding: 30px; margin: 20px 0; background: linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(236, 72, 153, 0.1));">
          <div style="background: rgba(168, 85, 247, 0.2); color: #a855f7; font-size: 12px; padding: 5px 15px; border-radius: 20px; display: inline-block; margin-bottom: 15px;">👑 EXECUTIVE TIER - LIMITED TO 2 NATIONWIDE</div>
          <h3 style="font-size: 24px; font-weight: bold; margin-bottom: 10px;">The <span style="color: #a855f7;">"Power 2"</span> Super Soldier</h3>
          <p style="color: #9ca3af; margin-bottom: 20px;">High-level executive position. Each Super Soldier manages a <span style="color: #a855f7; font-weight: bold;">25-Soldier Platoon</span> and earns 3% lifetime management override.</p>
          
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px;">
            <div style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; text-align: center;">
              <div style="color: #a855f7; font-size: 20px; font-weight: bold;">25</div>
              <div style="color: #9ca3af; font-size: 11px;">Soldiers per Platoon</div>
            </div>
            <div style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; text-align: center;">
              <div style="color: #10b981; font-size: 20px; font-weight: bold;">3%</div>
              <div style="color: #9ca3af; font-size: 11px;">Lifetime Override</div>
            </div>
            <div style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; text-align: center;">
              <div style="color: #f59e0b; font-size: 20px; font-weight: bold;">$56K</div>
              <div style="color: #9ca3af; font-size: 11px;">Max Monthly (Yr 3)</div>
            </div>
            <div style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; text-align: center;">
              <div style="color: #ec4899; font-size: 20px; font-weight: bold;">85%</div>
              <div style="color: #9ca3af; font-size: 11px;">Min Retention KPI</div>
            </div>
          </div>
          
          <div style="font-size: 12px; color: #9ca3af; padding: 10px; background: rgba(0,0,0,0.2); border-radius: 8px;">
            <strong style="color: #f59e0b;">Golden Handcuff:</strong> 3% override is lifetime residual while in active management. Reverts to company if Super Soldier resigns or fails KPIs, protecting founder's 77% gross margin.
          </div>
        </div>

        <div class="grid-3" style="margin-top: 20px;">
          <div class="card" style="border-color: rgba(249, 115, 22, 0.3);">
            <div style="font-size: 24px; margin-bottom: 10px;">👥</div>
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">Word of Mouth</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Hair shows & trade events</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Salon-to-salon referrals</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Barber shop networks</div>
          </div>
          <div class="card" style="border-color: rgba(236, 72, 153, 0.3);">
            <div style="font-size: 24px; margin-bottom: 10px;">✨</div>
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">Social Media</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Instagram & TikTok content</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Influencer partnerships</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Before/after showcases</div>
          </div>
          <div class="card" style="border-color: rgba(168, 85, 247, 0.3);">
            <div style="font-size: 24px; margin-bottom: 10px;">🏪</div>
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">Salon Stores</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Point-of-sale presence</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Product bundling deals</div>
            <div style="font-size: 12px; color: #9ca3af;"><span class="bullet"></span>Training partnerships</div>
          </div>
        </div>
      </div>

      <!-- Slide 10: Technical Roadmap -->
      <div class="slide">
        <div class="section-label green">For Developers</div>
        <h1 class="title">Technical <span class="green">Roadmap</span></h1>
        <p class="subtitle">Built on modern, scalable architecture</p>
        <div class="grid-4" style="margin-bottom: 40px;">
          <div class="card text-center" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 28px; margin-bottom: 10px;">🌐</div>
            <div style="font-size: 12px; color: #10b981;">Platform</div>
            <div style="font-weight: bold;">Scalable PWA</div>
          </div>
          <div class="card text-center" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 28px; margin-bottom: 10px;">💳</div>
            <div style="font-size: 12px; color: #10b981;">Payments</div>
            <div style="font-weight: bold;">Stripe + Multi-Gateway</div>
          </div>
          <div class="card text-center" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 28px; margin-bottom: 10px;">🔗</div>
            <div style="font-size: 12px; color: #10b981;">API</div>
            <div style="font-weight: bold;">RESTful, Integration Ready</div>
          </div>
          <div class="card text-center" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="font-size: 28px; margin-bottom: 10px;">🗄️</div>
            <div style="font-size: 12px; color: #10b981;">Database</div>
            <div style="font-weight: bold;">MongoDB</div>
          </div>
        </div>
        <div class="grid-4">
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="background: rgba(16, 185, 129, 0.2); color: #10b981; font-size: 12px; padding: 5px 10px; border-radius: 20px; display: inline-block; margin-bottom: 10px;">Q1 2025</div>
            <div class="card-title" style="font-size: 18px;">Platform Launch</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Core booking system</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Payment integration</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Loyalty engine v1</div>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="background: rgba(16, 185, 129, 0.2); color: #10b981; font-size: 12px; padding: 5px 10px; border-radius: 20px; display: inline-block; margin-bottom: 10px;">Q2 2025</div>
            <div class="card-title" style="font-size: 18px;">Scale</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Mobile apps (iOS/Android)</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Advanced analytics</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Multi-location support</div>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="background: rgba(16, 185, 129, 0.2); color: #10b981; font-size: 12px; padding: 5px 10px; border-radius: 20px; display: inline-block; margin-bottom: 10px;">Q3 2025</div>
            <div class="card-title" style="font-size: 18px;">Expand</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Travel/leisure partnerships</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>AI recommendations</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>International markets</div>
          </div>
          <div class="card" style="border-color: rgba(16, 185, 129, 0.3);">
            <div style="background: rgba(16, 185, 129, 0.2); color: #10b981; font-size: 12px; padding: 5px 10px; border-radius: 20px; display: inline-block; margin-bottom: 10px;">Q4 2025</div>
            <div class="card-title" style="font-size: 18px;">Dominate</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>10,000+ active chairs</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>Enterprise API</div>
            <div style="font-size: 14px; color: #9ca3af;"><span class="bullet"></span>White-label solutions</div>
          </div>
        </div>
      </div>

      <!-- Slide 10: Investment Ask -->
      <div class="slide">
        <div class="section-label gold">The Ask</div>
        <h1 class="title">Investment <span class="gold">Opportunity</span></h1>
        <div class="card" style="border-color: rgba(245, 158, 11, 0.3); padding: 50px; margin-top: 40px;">
          <div class="text-center mb-8">
            <div style="background: rgba(245, 158, 11, 0.2); color: #f59e0b; font-size: 14px; padding: 8px 20px; border-radius: 20px; display: inline-block; margin-bottom: 20px;">🎯 The Goal</div>
            <div style="font-size: 56px; font-weight: bold;">10,000+ "Chairs"</div>
            <p style="color: #9ca3af;">Scaling the platform in the next 18 months</p>
          </div>
          <div class="grid-2" style="grid-template-columns: 1fr 1fr 1fr; margin: 40px 0;">
            <div style="text-align: center; padding: 30px; background: rgba(0,0,0,0.3); border-radius: 12px;">
              <div style="font-size: 36px; margin-bottom: 10px;">💻</div>
              <div style="font-weight: bold; margin-bottom: 10px;">Engineering</div>
              <p style="font-size: 14px; color: #9ca3af;">Advanced features, mobile apps, AI integration</p>
            </div>
            <div style="text-align: center; padding: 30px; background: rgba(0,0,0,0.3); border-radius: 12px;">
              <div style="font-size: 36px; margin-bottom: 10px;">📈</div>
              <div style="font-weight: bold; margin-bottom: 10px;">Marketing</div>
              <p style="font-size: 14px; color: #9ca3af;">Targeted campaigns to salon owners nationwide</p>
            </div>
            <div style="text-align: center; padding: 30px; background: rgba(0,0,0,0.3); border-radius: 12px;">
              <div style="font-size: 36px; margin-bottom: 10px;">🤝</div>
              <div style="font-weight: bold; margin-bottom: 10px;">Partnerships</div>
              <p style="font-size: 14px; color: #9ca3af;">Leisure-sector and travel industry integrations</p>
            </div>
          </div>
          <p style="text-align: center; font-size: 18px; margin-top: 30px;">
            MyHairChair sits at the intersection of <span class="gold" style="font-weight: bold;">FinTech</span> (rent/payments) 
            and <span class="gold" style="font-weight: bold;">AdTech</span> (loyalty/marketing).
          </p>
        </div>
      </div>

      <!-- Slide 11: Contact -->
      <div class="slide text-center">
        <h1 class="title">Let's Build the <span class="gold">Future Together</span></h1>
        <div class="card" style="border-color: rgba(245, 158, 11, 0.3); padding: 60px; max-width: 600px; margin: 40px auto;">
          <div class="founder-circle">GB</div>
          <div style="font-size: 28px; font-weight: bold; margin-bottom: 10px;">George Broughton III</div>
          <div class="gold" style="font-size: 18px; margin-bottom: 5px;">Founder & CEO</div>
          <div style="font-size: 14px; color: #6b7280; margin-bottom: 30px;">MyHairChair | A Gonowbaby Media Group LLC Company</div>
          <div style="background: rgba(245, 158, 11, 0.2); padding: 15px 30px; border-radius: 12px; display: inline-block; margin-bottom: 15px;">
            <span style="font-size: 18px;">📧 info@myhairchair.com</span>
          </div>
          <br/>
          <div style="background: rgba(245, 158, 11, 0.1); padding: 15px 30px; border-radius: 12px; display: inline-block;">
            <span style="font-size: 18px;">🌐 MyHairChair.com</span>
          </div>
        </div>
        <div style="display: flex; align-items: center; justify-content: center; gap: 15px; margin-top: 40px;">
          <div class="logo-circle" style="width: 50px; height: 50px; font-size: 20px;">✂️</div>
          <span style="font-size: 24px; font-weight: bold; color: #f59e0b;">MyHairChair</span>
        </div>
        <p style="margin-top: 20px; color: #6b7280; font-style: italic;">"The Chair is the center of the beauty economy."</p>
        <p style="margin-top: 40px; color: #4b5563; font-size: 12px;">© 2025 MyHairChair | A Gonowbaby Media Group LLC Company</p>
      </div>
    `;

    document.body.appendChild(pdfContent);

    const options = {
      margin: 0,
      filename: 'MyHairChair_Investor_Deck_2025.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
      pagebreak: { mode: 'css', before: '.slide', avoid: '.card' }
    };

    try {
      await html2pdf().set(options).from(pdfContent).save();
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      document.body.removeChild(pdfContent);
      setIsGeneratingPDF(false);
    }
  };

  const marketStats = [
    { value: "$500B+", label: "Global Beauty Market", icon: Globe },
    { value: "$7.5B", label: "Black Beauty Spend (Annual)", icon: Crown },
    { value: "5th", label: "Latino GDP World Rank", icon: TrendingUp },
    { value: "9x", label: "Higher Per-Capita Spend", icon: BarChart3 }
  ];

  const problems = [
    {
      persona: "Salon Owners",
      pain: "Struggling to track and collect booth rent manually. Chasing payments, paper calendars, and missed revenue.",
      icon: Building2
    },
    {
      persona: "Stylists & Barbers",
      pain: "Lack professional tools to manage loyalty programs, payments, and client relationships.",
      icon: Scissors
    },
    {
      persona: "Clients",
      pain: "No central portal to manage their beauty leisure experience, track rewards, or book seamlessly.",
      icon: Users
    }
  ];

  const solutions = [
    {
      title: "Automated Rent Collection",
      description: "Seamless booth rent collection with recurring billing, reminders, and instant payment tracking for owners.",
      icon: DollarSign
    },
    {
      title: "The Command Center",
      description: "A personal business suite for independent stylists - services, pricing, scheduling, and client management.",
      icon: Target
    },
    {
      title: "Loyalty Engine",
      description: "Customizable point systems that drive retention. Stylists set their values, owners control commissions.",
      icon: Gift
    },
    {
      title: "Leisure Integration",
      description: "Booking built for the entertainment and travel lifestyle. Beauty is the first step in the leisure journey.",
      icon: Calendar
    }
  ];

  const techStack = [
    { label: "Platform", value: "Scalable Web App (PWA)", icon: Globe },
    { label: "Payments", value: "Stripe + Multi-Gateway", icon: CreditCard },
    { label: "API", value: "RESTful, Integration Ready", icon: Code },
    { label: "Database", value: "MongoDB (Granular Tracking)", icon: Database }
  ];

  const roadmapItems = [
    { phase: "Q1 2025", title: "Platform Launch", items: ["Core booking system", "Payment integration", "Loyalty engine v1"] },
    { phase: "Q2 2025", title: "Scale", items: ["Mobile apps (iOS/Android)", "Advanced analytics", "Multi-location support"] },
    { phase: "Q3 2025", title: "Expand", items: ["Travel/leisure partnerships", "AI-powered recommendations", "International markets"] },
    { phase: "Q4 2025", title: "Dominate", items: ["10,000+ active chairs", "Enterprise API", "White-label solutions"] }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* SEO Meta Tags for Social Sharing */}
      <SEOMeta 
        title="MyHairChair - Investor Opportunity | $18M ARR by Year 3"
        description="Join us in disrupting the $97B salon industry. 25% equity opportunity with projected $18M ARR by 2028. 1.1M+ addressable market."
        url="https://myhairchair.com/investors"
        image="https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/63d58951e5ab7e31ae4c087a16e2dd8d29cd67a8c02b18e2b7a2b5eca234ad44.png"
        type="article"
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Scissors className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
            </Link>
            <div className="flex items-center gap-4">
              {/* Share Button */}
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-amber-400 flex items-center gap-2"
                onClick={() => handleShare('native')}
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Link to="/">
                <Button variant="ghost" className="text-gray-300 hover:text-amber-400">
                  Back to App
                </Button>
              </Link>
              <a href="mailto:info@myhairchair.com">
                <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Slide 1 */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Gold lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
        <div className="absolute bottom-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-8">
            <Crown className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Investor Presentation</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">MyHairChair</span>
            <br />
            <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 bg-clip-text text-transparent">
              The Future of Salon Commerce
            </span>
          </h1>

          <p className="text-xl text-gray-400 mb-4 max-w-3xl mx-auto">
            Empowering the Art of the Chair
          </p>

          <p className="text-lg text-amber-200/80 mb-8 max-w-2xl mx-auto">
            &ldquo;The Chair is the center of the beauty economy.&rdquo;
          </p>

          <p className="text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            MyHairChair is a comprehensive ecosystem designed for the modern era of travel, leisure, and professional grooming. 
            We aren&apos;t just an app — <span className="text-amber-400 font-semibold">we are the infrastructure for the $500B+ global beauty and leisure market.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              onClick={handleDownloadClick}
              disabled={isGeneratingPDF}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black text-lg px-10 py-6 rounded-xl shadow-xl shadow-amber-500/30 font-bold"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : hasAccess ? (
                <>
                  <Unlock className="w-5 h-5 mr-2" />
                  Download Pitch Deck (PDF)
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Get Instant Access
                </>
              )}
            </Button>
            <a href="#opportunity">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-lg px-8 py-6 rounded-xl">
                <FileText className="w-5 h-5 mr-2" />
                View Online
              </Button>
            </a>
            <a href="mailto:info@myhairchair.com">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-lg px-8 py-6 rounded-xl">
                <Mail className="w-5 h-5 mr-2" />
                Get in Touch
              </Button>
            </a>
          </div>

          {/* Access Granted Badge */}
          {hasAccess && (
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/50 px-4 py-2 rounded-full mb-8">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">
                Welcome back, {localStorage.getItem('investor_name') || 'Investor'}! Full access granted.
              </span>
            </div>
          )}

          {/* Share This Presentation */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <p className="text-gray-400 text-sm">Share this opportunity</p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2"
                onClick={() => handleShare('copy')}
                title="Copy Link"
              >
                <Copy className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 rounded-full p-2"
                onClick={() => handleShare('twitter')}
                title="Share on X/Twitter"
              >
                <Twitter className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 rounded-full p-2"
                onClick={() => handleShare('linkedin')}
                title="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#1877F2] hover:bg-[#1877F2]/10 rounded-full p-2"
                onClick={() => handleShare('facebook')}
                title="Share on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#25D366] hover:bg-[#25D366]/10 rounded-full p-2"
                onClick={() => handleShare('whatsapp')}
                title="Share on WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-amber-400 hover:bg-amber-400/10 rounded-full p-2"
                onClick={() => handleShare('email')}
                title="Share via Email"
              >
                <Mail className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Founder Info */}
          <div className="inline-flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/10">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full flex items-center justify-center text-black font-bold text-xl">
              GB
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">George Broughton III</p>
              <p className="text-sm text-amber-400">Founder & CEO</p>
              <p className="text-xs text-gray-500">MyHairChair | A Gonowbaby Media Group LLC Company</p>
            </div>
          </div>

          {/* Hero Image - Diverse Salon Team */}
          <div className="mt-16 relative rounded-2xl overflow-hidden ring-2 ring-amber-500/30 shadow-2xl shadow-amber-500/20 max-w-5xl mx-auto">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/c43c5488bf83fdef4a3330e9e97845b97e650a5261c54897b26be1f8a254a4c1.png"
              alt="Diverse team of stylists and barbers at MyHairChair salon"
              className="w-full h-[300px] sm:h-[400px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg">
                <p className="text-amber-400 font-semibold">The MyHairChair Experience</p>
                <p className="text-gray-300 text-sm">Diverse teams. Premium service. Modern technology.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Access Form Modal */}
        <Dialog open={showAccessForm} onOpenChange={setShowAccessForm}>
          <DialogContent className="bg-gray-900 border-amber-500/30 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
                  Get Instant Access
                </span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-black" />
              </div>
              <p className="text-gray-400">
                Enter your details to instantly access the full investor pitch deck
              </p>
            </div>

            <form onSubmit={handleAccessSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Full Name *</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Email Address *</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Company (Optional)</Label>
                <div className="relative mt-2">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Acme Ventures"
                    value={leadForm.company}
                    onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold py-6 rounded-xl"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5 mr-2" />
                    Get Instant Access
                  </>
                )}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              By submitting, you agree to receive communications about MyHairChair investment opportunities.
            </p>
          </DialogContent>
        </Dialog>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-amber-500/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-amber-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Market Stats Bar */}
      <section className="py-16 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {marketStats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-black/70 mx-auto mb-2" />
                <div className="text-3xl md:text-4xl font-bold text-black">{stat.value}</div>
                <div className="text-black/70 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET DISRUPTION - Competitor Analysis */}
      <section className="py-24 bg-gradient-to-b from-black via-red-950/20 to-black relative" id="disruption">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-red-400 font-medium mb-4 block tracking-widest uppercase">Market Disruption</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Capturing <span className="text-red-400">1.1M+ Underserved</span> Professionals
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              The Big Four platforms charge 2.6-3.5% processing fees and lock professionals into rigid payment systems. 
              MyHairChair offers payment freedom and zero platform fees.
            </p>
          </div>

          {/* Competitor User Base Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { name: "Square", users: "600K+", focus: "Solo-Pros & Small Shops", color: "from-gray-600 to-gray-800", fee: "2.6% + 10¢" },
              { name: "Vagaro", users: "200K+", focus: "Enterprise & Suites", color: "from-purple-600 to-purple-800", fee: "2.75% + 25¢" },
              { name: "StyleSeat", users: "200K+", focus: "Marketplace Focus", color: "from-pink-600 to-pink-800", fee: "2.75% + 25¢" },
              { name: "Booksy", users: "100K+", focus: "Barber Community", color: "from-blue-600 to-blue-800", fee: "2.49% + 15¢" }
            ].map((competitor, idx) => (
              <div key={idx} className="relative group">
                <div className={`bg-gradient-to-br ${competitor.color} rounded-2xl p-6 border border-white/10 hover:border-red-500/50 transition-all`}>
                  <div className="text-3xl font-bold text-white mb-1">{competitor.users}</div>
                  <div className="text-lg font-semibold text-white/90 mb-2">{competitor.name}</div>
                  <div className="text-sm text-white/60 mb-3">{competitor.focus}</div>
                  <div className="flex items-center gap-2 text-red-300 text-sm">
                    <DollarSign className="w-4 h-4" />
                    <span>{competitor.fee} per transaction</span>
                  </div>
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  ↗
                </div>
              </div>
            ))}
          </div>

          {/* Total Addressable Market */}
          <div className="text-center mb-16 p-8 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 rounded-3xl border border-red-500/30">
            <div className="text-6xl font-bold bg-gradient-to-r from-red-400 via-orange-400 to-amber-400 bg-clip-text text-transparent mb-4">
              1.1M+ U.S. Professionals
            </div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Currently penalized by high processing fees and rigid payment gateways. 
              <span className="text-amber-400 font-semibold"> Our target: 10-15% market capture within 3 years.</span>
            </p>
          </div>

          {/* Fee Comparison Matrix */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              <span className="text-red-400">Zero-Fee</span> Competitive Matrix
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-red-500/20 to-orange-500/20">
                    <th className="p-4 text-left text-white font-bold border-b border-red-500/30">Feature</th>
                    <th className="p-4 text-center text-amber-400 font-bold border-b border-red-500/30 bg-amber-500/10">MyHairChair</th>
                    <th className="p-4 text-center text-gray-400 border-b border-red-500/30">Square</th>
                    <th className="p-4 text-center text-gray-400 border-b border-red-500/30">StyleSeat</th>
                    <th className="p-4 text-center text-gray-400 border-b border-red-500/30">Vagaro</th>
                    <th className="p-4 text-center text-gray-400 border-b border-red-500/30">Booksy</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "Processing Fee", mhc: "0% (Zelle/CashApp)", square: "2.6% + 10¢", styleseat: "2.75% + 25¢", vagaro: "2.75% + 25¢", booksy: "2.49% + 15¢" },
                    { feature: "Payout Speed", mhc: "Instant", square: "1-2 Days", styleseat: "2-3 Days", vagaro: "1-2 Days", booksy: "1-2 Days" },
                    { feature: "Accept Zelle", mhc: "✓", square: "✗", styleseat: "✗", vagaro: "✗", booksy: "✗" },
                    { feature: "Accept CashApp", mhc: "✓", square: "✗", styleseat: "✗", vagaro: "✗", booksy: "✗" },
                    { feature: "Accept PayPal", mhc: "✓", square: "✗", styleseat: "✗", vagaro: "✓", booksy: "✗" },
                    { feature: "Cash Tracking", mhc: "✓", square: "✓", styleseat: "✗", vagaro: "✓", booksy: "✗" },
                    { feature: "Medical Scalp Logs", mhc: "✓", square: "✗", styleseat: "✗", vagaro: "✗", booksy: "✗" },
                    { feature: "Health-Aware Profiles", mhc: "✓", square: "✗", styleseat: "✗", vagaro: "✗", booksy: "✗" },
                    { feature: "Staff Commission Tracking", mhc: "✓", square: "✓", styleseat: "✗", vagaro: "✓", booksy: "✗" }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                      <td className="p-4 text-gray-300 font-medium">{row.feature}</td>
                      <td className="p-4 text-center bg-amber-500/5">
                        {row.mhc === "✓" ? (
                          <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                        ) : row.mhc === "✗" ? (
                          <span className="text-red-400">✗</span>
                        ) : (
                          <span className="text-amber-400 font-bold text-sm">{row.mhc}</span>
                        )}
                      </td>
                      <td className="p-4 text-center text-gray-500 text-sm">{row.square}</td>
                      <td className="p-4 text-center text-gray-500 text-sm">{row.styleseat}</td>
                      <td className="p-4 text-center text-gray-500 text-sm">{row.vagaro}</td>
                      <td className="p-4 text-center text-gray-500 text-sm">{row.booksy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Market Capture Roadmap */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-500/30 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Market Capture <span className="text-red-400">Roadmap</span>
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-red-500/10 rounded-2xl border border-red-500/20">
                <div className="text-4xl font-bold text-red-400 mb-2">Year 1</div>
                <div className="text-2xl font-bold text-white mb-2">15,000 Users</div>
                <div className="text-lg font-bold text-emerald-400 mb-4">$1.8M ARR</div>
                <ul className="text-gray-400 text-sm space-y-2 text-left">
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /> HairCare Soldiers referral army</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /> Trade show presence (Bronner Bros, IBS)</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /> Barber school partnerships</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /> 4,000 paid subscribers</li>
                </ul>
              </div>
              <div className="text-center p-6 bg-orange-500/10 rounded-2xl border border-orange-500/20">
                <div className="text-4xl font-bold text-orange-400 mb-2">Year 2</div>
                <div className="text-2xl font-bold text-white mb-2">60,000 Users</div>
                <div className="text-lg font-bold text-emerald-400 mb-4">$6.5M ARR</div>
                <ul className="text-gray-400 text-sm space-y-2 text-left">
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" /> Influencer partnerships</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" /> Regional marketing campaigns</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" /> Salon suite chain partnerships</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" /> 15,000 paid subscribers</li>
                </ul>
              </div>
              <div className="text-center p-6 bg-amber-500/10 rounded-2xl border border-amber-500/20">
                <div className="text-4xl font-bold text-amber-400 mb-2">Year 3</div>
                <div className="text-2xl font-bold text-white mb-2">150,000 Users</div>
                <div className="text-lg font-bold text-emerald-400 mb-4">$18M ARR</div>
                <ul className="text-gray-400 text-sm space-y-2 text-left">
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> 13.6% market capture achieved</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> National brand recognition</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> Enterprise salon chains</li>
                  <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> 40,000 paid subscribers</li>
                </ul>
              </div>
            </div>
            {/* Revenue Summary */}
            <div className="mt-8 bg-gradient-to-r from-emerald-500/10 via-green-500/5 to-emerald-500/10 rounded-2xl p-6 border border-emerald-500/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-emerald-400">$26.3M</div>
                  <div className="text-gray-400 text-sm">3-Year Total Revenue</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-400">$20.2M</div>
                  <div className="text-gray-400 text-sm">3-Year Net Profit</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-400">77%+</div>
                  <div className="text-gray-400 text-sm">Profit Margins</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400">$72M</div>
                  <div className="text-gray-400 text-sm">Exit Valuation (4x ARR)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Traction - LIVE STATS */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 relative" id="traction">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-transparent to-cyan-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-500/40 px-4 py-2 rounded-full mb-4">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              <span className="text-cyan-400 text-sm font-medium">Live Platform Data</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Real <span className="text-cyan-400">Traction</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              MyHairChair is already building momentum with real users in the beauty ecosystem.
            </p>
          </div>

          {platformStats ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className="bg-gradient-to-br from-cyan-500/10 to-transparent border-cyan-500/30 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-1">
                    {platformStats.platform_stats?.total_users || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Total Users</p>
                  {platformStats.platform_stats?.new_users_30d > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs">
                      <TrendingUp className="w-3 h-3" />
                      +{platformStats.platform_stats.new_users_30d} this month
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <Scissors className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-1">
                    {platformStats.platform_stats?.total_stylists || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Beauty Professionals</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {platformStats.platform_stats?.active_stylists || 0} with active services
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <Calendar className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-1">
                    {platformStats.transaction_stats?.total_appointments || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Appointments Booked</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {platformStats.engagement_stats?.avg_completion_rate}% completion rate
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <Gift className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-1">
                    {platformStats.engagement_stats?.total_referrals || 0}
                  </div>
                  <p className="text-gray-400 text-sm">Referrals Generated</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Organic growth through word-of-mouth
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
            </div>
          )}

          {/* Revenue Potential */}
          {platformStats?.transaction_stats?.estimated_gmv > 0 && (
            <div className="mt-8">
              <Card className="bg-gradient-to-r from-emerald-500/10 via-green-500/5 to-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
                        <DollarSign className="w-8 h-8 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Estimated Transaction Volume</p>
                        <p className="text-3xl font-bold text-white">
                          ${platformStats.transaction_stats.estimated_gmv.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="text-gray-400 text-sm mb-1">Completed Appointments</p>
                      <p className="text-2xl font-bold text-emerald-400">
                        {platformStats.transaction_stats?.completed_appointments || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Beta Badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-gray-800/50 border border-gray-700 rounded-2xl px-6 py-3">
              <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-gray-300">Currently in <span className="text-amber-400 font-semibold">Beta</span> - Building & Validating with Real Users</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem - Slide 2 */}
      <section className="py-24 bg-black relative" id="problem">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-red-400 font-medium mb-4 block tracking-widest uppercase">The Problem</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The <span className="text-red-400">Friction</span> in the Industry
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The beauty industry operates on outdated systems, leaving money and opportunity on the table.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <Card key={index} className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20 hover:border-red-500/40 transition-all">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-6">
                    <problem.icon className="w-7 h-7 text-red-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{problem.persona}</h3>
                  <p className="text-gray-400">{problem.pain}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Solution - Slide 3 */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-medium mb-4 block tracking-widest uppercase">The Solution</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              MyHairChair <span className="text-emerald-400">Solves Everything</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A complete platform that connects owners, stylists, and clients in one powerful ecosystem.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <Card key={index} className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/20 hover:border-emerald-500/40 transition-all group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                    <solution.icon className="w-6 h-6 text-emerald-400 group-hover:text-black transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{solution.title}</h3>
                  <p className="text-gray-400 text-sm">{solution.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Loyalty Engine - Slide 4 */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">The Innovation</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                The <span className="text-amber-400">&ldquo;Loyalty Engine&rdquo;</span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Our proprietary loyalty system puts the power in the hands of the professionals while maximizing client retention.
              </p>

              <div className="space-y-4">
                {[
                  { title: "Empowered Stylists", desc: "Independent pros set their own point values (5pts per cut, 15pts per color)" },
                  { title: "Owner Control", desc: "Master-switch for commission-based or booth-rent models" },
                  { title: "Smart Exclusions", desc: "Built-in rewards that exclude high-labor costs (braids/weaves) to protect margins" },
                  { title: "Status-Based Rewards", desc: "Tier system (Bronze → Platinum) drives repeat visits and loyalty" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-sm text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Loyalty Progress Visualization */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 border border-amber-500/20">
              <div className="text-center mb-8">
                <Gift className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white">Point Tracking System</h3>
                <p className="text-gray-400 text-sm">Client Progress to Free Service</p>
              </div>

              {/* Progress Bar Visualization */}
              <div className="space-y-6">
                <div className="bg-black/50 rounded-xl p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Current Points</span>
                    <span className="text-amber-400 font-bold">75 / 100</span>
                  </div>
                  <div className="h-4 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full relative">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">25 points to FREE Haircut!</p>
                </div>

                {/* Tier Badges */}
                <div className="grid grid-cols-4 gap-2">
                  {["Bronze", "Silver", "Gold", "Platinum"].map((tier, idx) => (
                    <div key={tier} className={`text-center p-3 rounded-lg ${idx <= 1 ? 'bg-amber-500/20 border border-amber-500/50' : 'bg-gray-800/50 border border-gray-700'}`}>
                      <Crown className={`w-5 h-5 mx-auto mb-1 ${idx <= 1 ? 'text-amber-400' : 'text-gray-600'}`} />
                      <p className={`text-xs font-medium ${idx <= 1 ? 'text-amber-400' : 'text-gray-500'}`}>{tier}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity - Slide 5 */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900 relative" id="opportunity">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Market Opportunity</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The <span className="text-amber-400">&ldquo;Power Consumer&rdquo;</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We target the highest-spending demographics in personal care and leisure.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Black & African American */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Crown className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Black & African American</h3>
                    <p className="text-purple-400">The &ldquo;Power&rdquo; Consumer</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-black/30 rounded-xl p-4">
                    <p className="text-3xl font-bold text-white mb-1">$7.5 Billion</p>
                    <p className="text-gray-400 text-sm">Annual beauty spend by Black women alone</p>
                  </div>

                  <div className="space-y-3">
                    {[
                      "9x higher per-capita spend on hair care",
                      "Cultural trendsetters - influence what becomes 'cool'",
                      "Highly responsive to loyalty & status-based rewards",
                      "Most engaged with beauty technology adoption"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <p className="text-gray-300 text-sm">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hispanic & Latino */}
            <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Hispanic & Latino</h3>
                    <p className="text-orange-400">The Fastest-Growing Consumer</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-black/30 rounded-xl p-4">
                    <p className="text-3xl font-bold text-white mb-1">5th Largest GDP</p>
                    <p className="text-gray-400 text-sm">If U.S. Latino market were a standalone country</p>
                  </div>

                  <div className="space-y-3">
                    {[
                      "Median age 30 vs. 44 (prime spending years)",
                      "Engine of American economy's growth",
                      "#1 consumers of cinema & family entertainment",
                      "High adoption of mobile-first experiences"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <p className="text-gray-300 text-sm">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-amber-500/10 border border-amber-500/30 rounded-2xl px-8 py-4">
              <p className="text-amber-400 font-semibold text-lg">
                The MyHairChair Advantage: We speak the language of the &ldquo;Power Consumer.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sterile Mate & Gonowbaby Edge - Slide 6 */}
      <section className="py-24 bg-black relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-medium mb-4 block tracking-widest uppercase">Leadership Experience</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The <span className="text-blue-400">Founder&apos;s</span> Advantage
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Sterile Mate */}
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Sterile Mate LLC</h3>
                    <p className="text-blue-400 text-sm">B2B Medical Supplies (Est. 2020)</p>
                  </div>
                </div>

                <p className="text-gray-300 mb-6">
                  Founded by George Broughton III during the COVID-19 pandemic, Sterile Mate is a 
                  <span className="text-blue-400 font-semibold"> separate B2B venture</span> focused on 
                  <span className="text-blue-400 font-semibold"> medical supplies for hospitals</span> and 
                  <span className="text-blue-400 font-semibold"> water treatment systems</span>.
                </p>

                <div className="space-y-2">
                  {["Proven entrepreneurial track record", "Industry-standard professionalism", "B2B operations experience"].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <p className="text-gray-300 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Gonowbaby */}
            <Card className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-pink-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Globe className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Gonowbaby Media Group LLC</h3>
                    <p className="text-pink-400 text-sm">Travel & Entertainment Network</p>
                  </div>
                </div>

                <p className="text-gray-300 mb-6">
                  MyHairChair&apos;s parent company. Integrated with Gonowbaby&apos;s travel and entertainment network, we position 
                  <span className="text-pink-400 font-semibold"> beauty as the first step in the &ldquo;Leisure Journey&rdquo;</span>.
                </p>

                <div className="space-y-2">
                  {["Travel & entertainment reach", "Leisure sector partnerships", "Multi-channel marketing network"].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-pink-400" />
                      <p className="text-gray-300 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <p className="text-xl text-gray-300">
              <span className="text-amber-400 font-semibold">The Synergy:</span> Beauty is the first step in the &ldquo;Leisure Journey.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Competitive Analysis - Slide 7 */}
      <section className="py-24 bg-black relative" id="competition">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-purple-400 font-medium mb-4 block tracking-widest uppercase">Market Position</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Competitive <span className="text-purple-400">Analysis</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              How MyHairChair stacks up against the competition
            </p>
          </div>

          {/* Comparison Table */}
          <Card className="bg-gray-900/50 border-purple-500/30 overflow-hidden mb-8">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-500/20 border-b border-purple-500/30">
                      <th className="text-left p-4 text-white font-semibold">Feature</th>
                      <th className="p-4 text-center text-amber-400 font-bold text-lg">MyHairChair</th>
                      <th className="p-4 text-center text-gray-400">Booksy</th>
                      <th className="p-4 text-center text-gray-400">StyleSeat</th>
                      <th className="p-4 text-center text-gray-400">Vagaro</th>
                      <th className="p-4 text-center text-gray-400">Square</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      // BOOKING & SCHEDULING
                      { feature: "Online Booking", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true, category: "Booking" },
                      { feature: "Guest Booking (No Account)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: true, highlight: true },
                      { feature: "Recurring Appointments", mhc: true, booksy: true, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "Walk-in Queue Management", mhc: true, booksy: false, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "Google Calendar Sync", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true },
                      { feature: "Smart Service Upsells", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      // MARKETING & CLIENT ACQUISITION
                      { feature: "QR Code Client Invites", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Digital Business Cards", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Digital Marketing Toolkit", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Pro Mini-Site Builder", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Stylist Marketplace", mhc: true, booksy: true, styleseat: true, vagaro: false, square: false },
                      // LOYALTY & RETENTION
                      { feature: "Loyalty/Rewards Engine", mhc: true, booksy: true, styleseat: true, vagaro: true, square: false },
                      { feature: "Multi-Tier Status System", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Referral Program", mhc: true, booksy: false, styleseat: true, vagaro: false, square: false },
                      { feature: "Client Notes & History", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true },
                      // HEALTH & PROFESSIONAL CARE
                      { feature: "Scalp Care Log & pH Tracking", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "Health-Aware Client Profiles", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      // BUSINESS MANAGEMENT
                      { feature: "Booth/Chair Rental Mgmt", mhc: true, booksy: false, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "Staff Commission Tracking", mhc: true, booksy: false, styleseat: false, vagaro: true, square: false },
                      { feature: "Revenue Analytics Dashboard", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true },
                      { feature: "Accounting Integration", mhc: true, booksy: false, styleseat: false, vagaro: true, square: true },
                      // AUTH & SECURITY
                      { feature: "Magic Link (Passwordless)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Google OAuth Login", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true },
                      { feature: "Profile Verification System", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      // PLATFORM
                      { feature: "PWA (App-Like Experience)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "AI Chatbot Assistant", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "In-App Messaging/Mailbox", mhc: true, booksy: true, styleseat: true, vagaro: true, square: false }
                    ].map((row, idx) => (
                      <tr key={idx} className={`border-b border-gray-800 ${row.highlight ? 'bg-amber-500/10' : ''}`}>
                        <td className={`p-4 ${row.highlight ? 'text-white font-semibold' : 'text-gray-300'}`}>
                          {row.feature}
                          {row.isNew && <span className="ml-2 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
                        </td>
                        <td className="p-4 text-center">
                          {row.mhc ? <CheckCircle className="w-6 h-6 text-green-400 mx-auto" /> : <span className="text-red-400 text-lg">✗</span>}
                        </td>
                        <td className="p-4 text-center">
                          {row.booksy ? <CheckCircle className="w-5 h-5 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-4 text-center">
                          {row.styleseat ? <CheckCircle className="w-5 h-5 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-4 text-center">
                          {row.vagaro ? <CheckCircle className="w-5 h-5 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-4 text-center">
                          {row.square ? <CheckCircle className="w-5 h-5 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Unique Advantages */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-8">
            {[
              { icon: Users, label: "Guest Booking", desc: "No signup needed" },
              { icon: QrCode, label: "QR Invites", desc: "Instant acquisition" },
              { icon: CreditCard, label: "Digital Cards", desc: "Modern networking" },
              { icon: Clock, label: "Walk-in Queue", desc: "Real-time mgmt" },
              { icon: Smartphone, label: "PWA Ready", desc: "App experience" },
              { icon: Calendar, label: "Recurring", desc: "Auto-rebooking" },
              { icon: Zap, label: "Magic Link", desc: "Passwordless" },
              { icon: Globe, label: "Pro Mini-Site", desc: "Personal website" }
            ].map((item, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30">
                <CardContent className="p-3 text-center">
                  <item.icon className="w-6 h-6 text-amber-400 mx-auto mb-1" />
                  <h4 className="text-white font-semibold text-xs">{item.label}</h4>
                  <p className="text-gray-500 text-[10px] mt-0.5">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/50 rounded-2xl px-8 py-4">
              <Trophy className="w-6 h-6 text-amber-400" />
              <span className="text-lg">
                <span className="text-amber-400 font-bold">23 Unique Features</span>
                <span className="text-gray-300"> that no single competitor offers together</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Strategy Section */}
      <section className="py-24 bg-black relative" id="marketing">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-red-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-orange-400 font-medium mb-4 block tracking-widest uppercase">Go-To-Market Strategy</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How We <span className="text-orange-400">Grow</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Word-of-mouth driven growth powered by industry insiders who earn lifetime revenue
            </p>
          </div>

          {/* HairCare Soldiers Hero */}
          <Card className="bg-gradient-to-br from-orange-500/10 via-red-500/10 to-purple-500/10 border-orange-500/30 mb-12">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 bg-orange-500/20 px-4 py-2 rounded-full mb-4">
                    <Shield className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-400 font-semibold">Our Secret Weapon</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    HairCare <span className="text-orange-400">Soldiers</span>
                  </h3>
                  <p className="text-gray-300 mb-6 text-lg">
                    An elite network of salon stylists, barbers, and owners who champion MyHairChair 
                    in their communities. They are our MVPs — earning <span className="text-green-400 font-bold">lifetime revenue</span> for 
                    every referral they bring to the platform.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="bg-black/30 px-4 py-2 rounded-lg">
                      <span className="text-orange-400 font-bold text-2xl">50</span>
                      <span className="text-gray-400 text-sm ml-2">Soldiers in Key Markets</span>
                    </div>
                    <div className="bg-black/30 px-4 py-2 rounded-lg">
                      <span className="text-green-400 font-bold text-2xl">∞</span>
                      <span className="text-gray-400 text-sm ml-2">Lifetime Commissions</span>
                    </div>
                  </div>
                  <Link to="/soldiers">
                    <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold">
                      <Users className="w-4 h-4 mr-2" />
                      Learn About HairCare Soldiers
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 md:w-64 md:h-64 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center shadow-2xl shadow-orange-500/30">
                    <div className="text-center">
                      <Shield className="w-16 h-16 md:w-24 md:h-24 text-white mx-auto mb-2" />
                      <span className="text-white font-bold text-lg md:text-xl">SOLDIERS</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marketing Channels */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-gradient-to-br from-orange-500/10 to-transparent border-orange-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Word of Mouth</h4>
                <p className="text-gray-400 mb-4">
                  Our best-case scenario. Industry professionals trust recommendations from their peers.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Hair shows & trade events
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Salon-to-salon referrals
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Barber shop networks
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-pink-500/10 to-transparent border-pink-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-pink-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Social Media</h4>
                <p className="text-gray-400 mb-4">
                  Targeted campaigns on platforms where stylists and clients connect.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Instagram & TikTok content
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Influencer partnerships
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Before/after showcases
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Salon Stores</h4>
                <p className="text-gray-400 mb-4">
                  Direct partnerships with salon supply stores and distributors.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Point-of-sale presence
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Product bundling deals
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Training partnerships
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Soldier Activities */}
          <div className="bg-gradient-to-r from-orange-500/10 via-red-500/5 to-orange-500/10 border border-orange-500/20 rounded-2xl p-8">
            <h4 className="text-xl font-bold text-white mb-6 text-center">What HairCare Soldiers Do</h4>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Calendar, title: "Hair Shows", desc: "Represent MyHairChair at major industry events" },
                { icon: Users, title: "Collaborate", desc: "Connect with fellow salon owners and stylists" },
                { icon: Gift, title: "Recruit", desc: "Bring new salons onto the platform" },
                { icon: DollarSign, title: "Earn", desc: "Lifetime revenue from every referral" }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-7 h-7 text-orange-400" />
                  </div>
                  <h5 className="text-white font-semibold mb-1">{item.title}</h5>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Roadmap - Slide 8 */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black relative" id="developers">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-green-400 font-medium mb-4 block tracking-widest uppercase">For Developers</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Technical <span className="text-green-400">Roadmap</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built on modern, scalable architecture ready for enterprise growth.
            </p>
          </div>

          {/* Tech Stack */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {techStack.map((tech, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 text-center hover:border-green-500/50 transition-colors">
                <tech.icon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-green-400 text-xs font-medium mb-1">{tech.label}</p>
                <p className="text-white font-semibold text-sm">{tech.value}</p>
              </div>
            ))}
          </div>

          {/* Roadmap Timeline */}
          <div className="grid md:grid-cols-4 gap-6">
            {roadmapItems.map((item, idx) => (
              <div key={idx} className="relative">
                {idx < 3 && (
                  <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-green-500/50 to-transparent"></div>
                )}
                <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-6 hover:border-green-500/50 transition-colors">
                  <div className="inline-block bg-green-500/20 text-green-400 text-xs font-bold px-3 py-1 rounded-full mb-3">
                    {item.phase}
                  </div>
                  <h3 className="text-white font-semibold mb-3">{item.title}</h3>
                  <ul className="space-y-2">
                    {item.items.map((listItem, listIdx) => (
                      <li key={listIdx} className="flex items-center gap-2 text-sm text-gray-400">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        {listItem}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Ask - Slide 8 */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5"></div>
        <div className="max-w-4xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">The Ask</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Investment <span className="text-amber-400">Opportunity</span>
            </h2>
          </div>

          <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
            <CardContent className="p-10">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-full mb-4">
                  <Target className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-semibold">The Goal</span>
                </div>
                <h3 className="text-4xl font-bold text-white mb-2">10,000+ &ldquo;Chairs&rdquo;</h3>
                <p className="text-gray-400">Scaling the platform in the next 18 months</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {[
                  { icon: Code, title: "Engineering", desc: "Advanced features, mobile apps, AI integration" },
                  { icon: TrendingUp, title: "Marketing", desc: "Targeted campaigns to salon owners nationwide" },
                  { icon: Building2, title: "Partnerships", desc: "Leisure-sector and travel industry integrations" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-black/30 rounded-xl p-6 text-center">
                    <item.icon className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <p className="text-gray-300 mb-6">
                  MyHairChair sits at the intersection of <span className="text-amber-400 font-semibold">FinTech</span> (rent/payments) 
                  and <span className="text-amber-400 font-semibold">AdTech</span> (loyalty/marketing).
                </p>
                <p className="text-lg text-white">
                  We are solving the &ldquo;unorganized&rdquo; nature of the booth-rental world by providing a 
                  <span className="text-amber-400 font-semibold"> &ldquo;Pro&rdquo; digital home</span> for the independent stylist.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact - Slide 9 */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900 relative" id="contact">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12">
            Let&apos;s Build the <span className="text-amber-400">Future Together</span>
          </h2>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-10 border border-amber-500/30">
            <div className="flex flex-col items-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full flex items-center justify-center text-black font-bold text-3xl mb-4 shadow-xl shadow-amber-500/30">
                GB
              </div>
              <h3 className="text-2xl font-bold text-white">George Broughton III</h3>
              <p className="text-amber-400">Founder & CEO</p>
              <p className="text-gray-500 text-sm">MyHairChair | A Gonowbaby Media Group LLC Company</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href="mailto:info@myhairchair.com">
                <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold px-8 py-6 rounded-xl">
                  <Mail className="w-5 h-5 mr-2" />
                  info@myhairchair.com
                </Button>
              </a>
              <a href="https://myhairchair.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8 py-6 rounded-xl">
                  <Globe className="w-5 h-5 mr-2" />
                  MyHairChair.com
                </Button>
              </a>
            </div>

            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center">
                <Scissors className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2025 MyHairChair | A Gonowbaby Media Group LLC Company
          </p>
          <p className="text-amber-400/50 text-xs mt-2">
            The Chair is the center of the beauty economy.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorsPage;
